import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") await login(email, password);
      else await register(email, name, password);
      toast.success("Benvenuto!");
      nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-sm">
          <div className="mb-10">
            <div className="mono-label mb-2">amdirDesk</div>
            <h1 className="font-display text-4xl font-medium tracking-tight text-neutral-900 leading-tight">
              Una direzione per<br/>le decisioni che contano.
            </h1>
          </div>

          <div className="flex gap-6 border-b border-neutral-200 mb-6">
            {["login", "register"].map((t) => (
              <button
                key={t}
                data-testid={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`pb-2 text-sm ${tab === t ? "brand-text border-b-2 border-current font-medium" : "text-neutral-500"}`}
              >
                {t === "login" ? "Accedi" : "Registrati"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            {tab === "register" && (
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input id="name" data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="input-email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" />
            </div>
            <div>
              <Label htmlFor="pw">Password</Label>
              <Input id="pw" type="password" data-testid="input-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" />
            </div>
            <Button type="submit" data-testid="submit-auth" disabled={loading} className="w-full brand-bg hover:opacity-90">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : tab === "login" ? "Accedi" : "Crea account"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-neutral-200" />
            <span className="mono-label">oppure</span>
            <div className="flex-1 h-px bg-neutral-200" />
          </div>

          <Button onClick={googleLogin} variant="outline" data-testid="google-login" className="w-full">
            Continua con Google
          </Button>

          <div className="mt-6 text-center">
            <a href="/portal" data-testid="link-client-portal" className="mono-label hover:brand-text underline">
              Sei un cliente? Entra nel portale
            </a>
          </div>
        </div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1765371513276-a74f1ecbcf7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBvZmZpY2UlMjBzdHVkaW98ZW58MHx8fHwxNzg5MDczNDM3fDA&ixlib=rb-4.1.0&q=85"
          alt="studio"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[color:var(--brand-primary)] opacity-30 mix-blend-multiply" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="mono-label text-white/80 mb-2">— amdirDesk</div>
          <p className="font-display text-2xl leading-snug">Competenze diverse. Uno sguardo condiviso.</p>
        </div>
      </div>
    </div>
  );
}
